import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Menu, X, ChevronDown, ChevronRight, Users, Building2, Wrench, Briefcase } from 'lucide-react';
import { cn } from '../lib/utils';

// Navigation structure
const NAV_ITEMS = [
  {
    label: 'For Landlords',
    href: '/landlords',
    icon: Building2,
    children: [
      { label: 'Overview', href: '/landlords', description: 'Why LetWise & how it works' },
      { label: 'Listing & Marketing', href: '/landlords/listing', description: 'Portals, add-ons, compliance' },
      { label: 'Compliance & Safety', href: '/landlords/compliance', description: 'Requirements & documents' },
      { label: 'Tenancy & Deposits', href: '/landlords/tenancy', description: 'Creation, signing, deposits' },
      { label: 'Maintenance & Repairs', href: '/landlords/maintenance', description: 'Work orders & approvals' },
      { label: 'Pricing', href: '/landlords/pricing', description: 'Packages & add-ons' },
      { label: 'FAQs', href: '/landlords/faqs', description: 'Common questions' },
    ]
  },
  {
    label: 'For Tenants',
    href: '/tenants',
    icon: Users,
    children: [
      { label: 'Overview', href: '/tenants', description: 'How applying works' },
      { label: 'Find & Apply', href: '/tenants/find-apply', description: 'Search, viewings, applications' },
      { label: 'Referencing', href: '/tenants/referencing', description: 'What\'s checked' },
      { label: 'Tenancy Journey', href: '/tenants/journey', description: 'Signing, payments, repairs' },
      { label: 'Moving In/Out', href: '/tenants/moving', description: 'Inventories & deposits' },
      { label: 'FAQs', href: '/tenants/faqs', description: 'Common questions' },
    ]
  },
  {
    label: 'For Contractors',
    href: '/contractors',
    icon: Wrench,
    children: [
      { label: 'Overview', href: '/contractors', description: 'How it works' },
      { label: 'Getting Started', href: '/contractors/getting-started', description: 'Sign up & vetting' },
      { label: 'Jobs & Scheduling', href: '/contractors/jobs', description: 'Accepting & managing work' },
      { label: 'Payments', href: '/contractors/payments', description: 'Quotes & invoicing' },
    ]
  },
  {
    label: 'For Agents',
    href: '/agents',
    icon: Briefcase,
  },
  {
    label: 'How It Works',
    href: '/how-it-works',
  },
  {
    label: 'Blog',
    href: '/blog',
  },
];

export function PublicNavbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Track current path for menu highlighting
  const prevPathname = React.useRef(location.pathname);
  
  // Close menu on route change (using ref to avoid lint warning)
  React.useLayoutEffect(() => {
    if (prevPathname.current !== location.pathname) {
      setMobileMenuOpen(false);
      setExpandedItem(null);
      prevPathname.current = location.pathname;
    }
  }, [location.pathname]);

  // Track scroll for header style
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <>
      {/* Header */}
      <header className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-200",
        scrolled ? "bg-white shadow-md" : "bg-white/95 backdrop-blur-sm"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0">
              <div className="p-1.5 sm:p-2 bg-primary rounded-lg">
                <HomeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-display font-extrabold text-primary">LetWise</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <div key={item.href} className="relative group">
                  <Link
                    to={item.href}
                    className={cn(
                      "flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      isActive(item.href)
                        ? "text-primary bg-primary/5"
                        : "text-slate-700 hover:text-primary hover:bg-slate-50"
                    )}
                  >
                    {item.label}
                    {item.children && <ChevronDown className="w-4 h-4" />}
                  </Link>
                  
                  {/* Desktop Dropdown */}
                  {item.children && (
                    <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="bg-white rounded-xl shadow-xl border p-2 min-w-[240px]">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={cn(
                              "block px-3 py-2.5 rounded-lg transition-colors",
                              isActive(child.href)
                                ? "bg-primary/5 text-primary"
                                : "hover:bg-slate-50"
                            )}
                          >
                            <div className="font-medium text-sm">{child.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{child.description}</div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/properties"
                className="text-sm font-medium text-slate-600 hover:text-primary transition-colors"
              >
                Browse Properties
              </Link>
              <Link
                to="/login"
                className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
              >
                Sign In
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 -mr-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={cn(
          "fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-200",
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setMobileMenuOpen(false)}
      />

      {/* Mobile Menu Drawer */}
      <div
        className={cn(
          "fixed top-14 right-0 bottom-0 z-50 w-full max-w-sm bg-white lg:hidden transition-transform duration-300 ease-out overflow-y-auto",
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <nav className="p-4 pb-24">
          {NAV_ITEMS.map((item) => (
            <div key={item.href} className="border-b border-slate-100 last:border-0">
              {item.children ? (
                <>
                  <button
                    onClick={() => setExpandedItem(expandedItem === item.href ? null : item.href)}
                    className="flex items-center justify-between w-full py-4 text-left"
                  >
                    <span className={cn(
                      "font-semibold",
                      isActive(item.href) ? "text-primary" : "text-slate-800"
                    )}>
                      {item.label}
                    </span>
                    <ChevronRight className={cn(
                      "w-5 h-5 text-slate-400 transition-transform",
                      expandedItem === item.href && "rotate-90"
                    )} />
                  </button>
                  <div className={cn(
                    "overflow-hidden transition-all duration-200",
                    expandedItem === item.href ? "max-h-[500px] pb-2" : "max-h-0"
                  )}>
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className={cn(
                          "block py-3 pl-4 pr-2 rounded-lg mb-1",
                          isActive(child.href)
                            ? "bg-primary/5 text-primary"
                            : "text-slate-600 hover:bg-slate-50"
                        )}
                      >
                        <div className="font-medium text-sm">{child.label}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{child.description}</div>
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <Link
                  to={item.href}
                  className={cn(
                    "block py-4 font-semibold",
                    isActive(item.href) ? "text-primary" : "text-slate-800"
                  )}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}

          {/* Mobile CTAs */}
          <div className="mt-6 space-y-3">
            <Link
              to="/properties"
              className="block w-full py-3 text-center font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Browse Properties
            </Link>
            <Link
              to="/login"
              className="block w-full py-3 text-center font-semibold text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/register"
              className="block w-full py-3 text-center font-semibold text-primary border-2 border-primary rounded-xl hover:bg-primary/5 transition-colors"
            >
              Create Account
            </Link>
          </div>
        </nav>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-14 sm:h-16" />
    </>
  );
}

export default PublicNavbar;
